import { useCallback, useState } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import FlashMessage from 'react-native-flash-message';
import { theme, Icon, Text, styles as baseStyles } from '@socrait/react';

import { ErrorBox } from '~/components/ErrorBox';
import { Loading } from '~/components/Loading';
import { ReviewActionItems } from '~/pages/review/ReviewActionItems';
import { ReviewScoreCards } from '~/pages/review/ReviewScoreCards';
import { ArchivedClassLayout } from '~/screens/archivedClass';
import { useReviewScorecards, useReviewActionItems, useWeeklyInsights } from '~/services/review';
import { useLogoutMutation } from '~/services/useLogoutMutation';
import { useUserProfile } from '~/services/useUserProfileQuery';
import { Period } from '~/services/types';
import { getFormattedDate } from '~/utils/date';

const isToday = (date: Date) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export type ArchivedClassReviewScreenProps = {
  activePeriod: Period | null;
  onClose: () => void;
  onRestore: (period: Period, ref?: React.RefObject<FlashMessage>) => void;
  isRestoring?: boolean;
};

export function ArchivedClassReviewScreen({
  activePeriod,
  onClose,
  onRestore,
  isRestoring = false,
}: ArchivedClassReviewScreenProps) {
  const [date, setDate] = useState(new Date());

  const {
    data: scorecardData,
    error: scorecardError,
    status: scorecardStatus,
  } = useReviewScorecards({
    date,
    periodId: activePeriod?.id ?? '',
  });

  const {
    data: actionItemsData,
    error: actionItemsError,
    status: actionItemsStatus,
  } = useReviewActionItems({
    date,
    periodId: activePeriod?.id ?? '',
  });

  const { data: weeklyInsightsData, status: weeklyInsightsStatus } = useWeeklyInsights({
    date,
    periodId: activePeriod?.id ?? '',
  });

  const { mutate: logout } = useLogoutMutation();
  const { user } = useUserProfile();

  const hasPraiseOrWarningItems = Boolean(
    scorecardData?.scorecards?.praises?.length || scorecardData?.scorecards?.warnings?.length,
  );

  const handlePrevDay = useCallback(() => {
    const currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() - 1);
    setDate(currentDate);
  }, [date]);

  const handleNextDay = useCallback(() => {
    const currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() + 1);
    setDate(currentDate);
  }, [date]);

  return (
    <ArchivedClassLayout period={activePeriod} onClose={onClose} onRestore={onRestore} isRestoring={isRestoring}>
      <View style={styles.outerContainer}>
        <View style={styles.leftContainer}>
          <View style={styles.dateContainer}>
            <Pressable onPress={handlePrevDay}>
              <Icon type="leftArrow" />
            </Pressable>
            <Text style={styles.date}>{getFormattedDate(date)}</Text>
            <Pressable onPress={handleNextDay} disabled={isToday(date)}>
              <Icon type="rightArrow" fill={isToday(date) ? theme.colors.gray : theme.colors.navy} />
            </Pressable>
          </View>
          {scorecardStatus === 'pending' ? (
            <Loading backgroundColor={theme.colors.transparent} />
          ) : scorecardError ? (
            <ErrorBox />
          ) : (
            <ReviewScoreCards scorecardData={scorecardData} />
          )}
          <View style={styles.userInfoContainer}>
            <Text style={styles.userInfoText}>{user?.email}</Text>
            <Pressable onPress={logout}>
              <Text style={[styles.userInfoText, styles.logoutText]}>Log Out</Text>
            </Pressable>
          </View>
        </View>
        <View style={styles.rightContainer}>
          {!!activePeriod ? (
            <ReviewActionItems
              date={date}
              hasPraiseOrWarningItems={hasPraiseOrWarningItems}
              actionItemsData={actionItemsData}
              actionItemsQueryStatus={actionItemsStatus}
              actionItemsError={actionItemsError}
              period={activePeriod}
              weeklyInsightsData={weeklyInsightsData}
              weeklyInsightsQueryStatus={weeklyInsightsStatus}
            />
          ) : (
            <ErrorBox text="Could not load period data. Please try refreshing the page." />
          )}
        </View>
      </View>
    </ArchivedClassLayout>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    backgroundColor: theme.colors.lightGray,
    position: 'relative',
  },
  leftContainer: {
    width: 370,
    height: '100%',
    maxHeight: '100%',
    backgroundColor: theme.colors.white,
    gap: theme.spacing.xlarge,
    paddingHorizontal: theme.spacing.xlarge,
    paddingVertical: theme.spacing.xlarge + theme.spacing.medium,
    overflowY: 'auto',
  },
  rightContainer: {
    height: '100%',
    flex: 1,
    paddingHorizontal: theme.spacing.xxlarge,
    paddingVertical: theme.spacing.xxlarge,
    backgroundColor: theme.colors.lightGray,
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.xxlarge,
    overflowY: 'auto',
  },
  logoutText: {
    fontSize: theme.fontSizes.xsmall,
    fontWeight: theme.fontWeights.bold,
    color: theme.colors.primaryPurple,
  },
  classInfo: {
    ...baseStyles.title,
    margin: 0,
  },
  dateContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.xlarge,
    marginVertical: theme.spacing.xlarge,
  },
  date: {
    fontSize: theme.fontSizes.medium,
    fontFamily: theme.fonts.medium,
    fontWeight: theme.fontWeights.medium,
  },
  userInfoContainer: {
    marginTop: 'auto',
    paddingTop: theme.spacing.medium,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  userInfoText: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.regular,
  },
});
